import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import React from 'react';
import { Body, Variant, Heading3 } from '~/components/atom/Typography';
import Link from '~/components/molecule/Link';
import AppDetailsContainer from '~/components/page/Apps/components/AppDetailsContainer';
import TEST_ID from './index.testid';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Field } from 'react-final-form';
import { widgetHelpLink } from '../../constants';
import type { onChangeArgs } from '../WidgetContent';
import Input from '~/components/molecule/Input';
import WidgetScript from '~/components/molecule/WidgetScript';
import { HELP_DESK_LINK } from '~/constants';
import VisibilityRulesEditor from './components/VisibilityRulesEditor';
import type { WidgetVisibilityRulesFragment } from '~/graphql/types';

export type Props = {
  onChange: (args: onChangeArgs<'settings'>) => void;
  googleAnalytics4?: string | null;
  visibilityRules: WidgetVisibilityRulesFragment;
} & RouteComponentProps;

const text = {
  title: 'Algemeen',
  installDescription: (
    <>
      De widget wordt op jouw website geplaatst en toont informatieproducten en
      diensten. Activeer Apps via het menu bovenaan, om deze toe te voegen aan
      de widget.
    </>
  ),
  installCta: (
    <>
      Publiceer de widget door onderstaande code toe te voegen aan je website.
    </>
  ),
  installLink: 'Bekijk hier de installatie en integratie instructies.',
  ga4Label: 'Google Analytics 4',
  ga4Description: 'Geef uw GA 4-ID op.',
  gaHelpLinkText: 'Zie dit Helpartikel voor meer informatie',
};

const Settings: React.FCC<Props> = ({
  dataTestId,
  onChange,
  googleAnalytics4,
  visibilityRules,
  ...rest
}) => (
  <AppDetailsContainer
    {...rest}
    data-testid={dataTestId}
    header={text.title}
    icon="gear"
    loading={false}
  >
    <JustificationContainer direction="column" width="100%" gap="s">
      <Body>{text.installDescription}</Body>
      <Body>
        {text.installCta}{' '}
        <Link to={widgetHelpLink} target="_blank">
          {text.installLink}
        </Link>
      </Body>
    </JustificationContainer>

    <WidgetScript />

    <VisibilityRulesEditor
      visibilityRules={visibilityRules}
      onChange={newRules => {
        onChange({
          pageKey: 'settings',
          fieldKey: 'visibilityRules',
          newValue: newRules,
        });
      }}
    />

    <JustificationContainer
      direction="column"
      width="100%"
      margin={['l', null, null, null]}
      gap="s"
    >
      <Heading3 variant={Variant.primary}>{text.ga4Label}</Heading3>
      <Body>
        {text.ga4Description}{' '}
        <Link
          to={`${HELP_DESK_LINK}/nl/articles/7854772-tracking-met-google-analytics-4`}
          target="_blank"
        >
          {text.gaHelpLinkText}
        </Link>
      </Body>
      <Field name="settings.googleAnalytics4">
        {({ meta: { submitting } }) => (
          <Input
            value={googleAnalytics4}
            width="100%"
            size="large"
            placeholder="G-..."
            dataTestId={TEST_ID.GOOGLE_ANALYTICS}
            disabled={submitting}
            onChange={event => {
              onChange({
                pageKey: 'settings',
                fieldKey: 'googleAnalytics4',
                newValue: event.target.value,
              });
            }}
          />
        )}
      </Field>
    </JustificationContainer>
  </AppDetailsContainer>
);

export default Settings;
